import { useState, useCallback } from 'react';
import { authService } from '../services/authService';
import { LoginCredentials, AuthResponse, TwoFactorChallenge, LoginResult } from '../types/auth';

export function useAuth() {
  const [user, setUser] = useState<AuthResponse['user'] | null>(() => {
    return authService.getUser();
  });
  const [awaitingOtp, setAwaitingOtp] = useState<TwoFactorChallenge | null>(null);

  const login = useCallback(
    async (credentials: LoginCredentials & { rememberMe?: boolean }): Promise<LoginResult> => {
      try {
        const response = await authService.login(credentials);
        // 2FA required: hold the challenge and let the UI prompt for the OTP.
        if ((response as any)?.twoFactorRequired) {
          setAwaitingOtp(response as TwoFactorChallenge);
          return response;
        }
        setUser((response as AuthResponse).user);
        return response;
      } catch (error) {
        throw error instanceof Error ? error : new Error('Invalid credentials');
      }
    },
    []
  );

  const verifyOtp = useCallback(
    async (code: string, rememberDevice: boolean): Promise<AuthResponse> => {
      if (!awaitingOtp) throw new Error('No pending verification');
      const response = await authService.verifyOtp({
        challengeId: awaitingOtp.challengeId,
        code,
        rememberDevice,
        preAuthToken: awaitingOtp.preAuthToken
      });
      setUser(response.user);
      setAwaitingOtp(null);
      return response;
    },
    [awaitingOtp]
  );

  const resendOtp = useCallback(async (): Promise<void> => {
    if (!awaitingOtp) throw new Error('No pending verification');
    await authService.resendOtp({
      challengeId: awaitingOtp.challengeId,
      preAuthToken: awaitingOtp.preAuthToken
    });
  }, [awaitingOtp]);

  const cancelOtp = useCallback(() => {
    setAwaitingOtp(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setAwaitingOtp(null);
    }
  }, []);

  const captcha = useCallback(async (token: string) => {
    try {
      await authService.captcha(token);
    } catch (error) {
      console.log('error:', error);
      throw new Error('Invalid captcha');
    }
  }, []);

  return {
    user,
    setUser,
    login,
    logout,
    captcha,
    isAuthenticated: !!user,
    awaitingOtp,
    verifyOtp,
    resendOtp,
    cancelOtp
  };
}
